
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { MdArrowBack, MdSave, MdEdit } from 'react-icons/md';
import './AddProduct.css'; // Reusing product form styles
import Swal from 'sweetalert2';
import axios from 'axios';
import { UPDATE_UNIT_API, VIEW_UNIT_API } from '../config/API';
import Loader from '../components/common/Loader';
import { showErrorToast } from '../utils/toastUtils';

const EditUnitMaster = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Detect if we are in 'view' mode (from path or state)
    const [isViewMode, setIsViewMode] = useState(
        location.pathname.includes('/view/') || location.state?.mode === 'view'
    );
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);

    const [formData, setFormData] = useState({
        unitName: '',
        status: 'active'
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setFetchingData(true);
                const token = localStorage.getItem('accessToken');

                const response = await axios.get(VIEW_UNIT_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                if (response.data.status) {
                    const unit = response.data.data;
                    setFormData({
                        unitName: unit.unitName || '',
                        status: unit.status || 'active'
                    });
                } else {
                    showErrorToast('Failed to fetch unit details');
                    navigate('/unit-master');
                }

            } catch (error) {
                console.error('Error fetching unit data:', error);
                showErrorToast('An error occurred while fetching data');
                navigate('/unit-master');
            } finally {
                setFetchingData(false);
            }
        };

        fetchData();
    }, [id, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isViewMode) return;

        if (!formData.unitName.trim()) {
            showErrorToast('Unit Name is required');
            return;
        }

        const result = await Swal.fire({
            title: 'Update Unit?',
            text: 'Do you want to save changes to this unit?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');

                const response = await axios.put(UPDATE_UNIT_API(id), formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Updated!',
                        text: 'Unit updated successfully',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    navigate('/unit-master');
                } else {
                    showErrorToast(response.data.error || response.data.message || 'Failed to update unit');
                }
            } catch (error) {
                console.error('Error updating unit:', error);
                const errorData = error.response?.data?.error || 'An error occurred while updating the unit';
                showErrorToast(errorData);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return <Loader fullPage text="Updating unit..." />;
    }

    return (
        <div className="add-product-page">
            <div className="page-header">
                <button onClick={() => navigate('/unit-master')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>{isViewMode ? 'Unit Details' : 'Edit Unit'}</h2>
                {isViewMode && (
                    <button onClick={() => setIsViewMode(false)} className="edit-toggle-btn" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                        <MdEdit /> Edit Mode
                    </button>
                )}
            </div>

            <form onSubmit={handleSubmit} className="product-form">
                <div className="form-section">
                    <h3>Unit Information</h3>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Unit Name <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="unitName"
                                value={formData.unitName}
                                onChange={handleChange}
                                placeholder="e.g. Kg"
                                disabled={isViewMode}
                            />
                        </div>

                        <div className="form-group">
                            <label>Status <span className="required-star">*</span></label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                disabled={isViewMode}
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/unit-master')} className="cancel-btn">
                        {isViewMode ? 'Back to List' : 'Cancel'}
                    </button>
                    {!isViewMode && (
                        <button type="submit" className="save-btn">
                            <MdSave /> Update Unit
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default EditUnitMaster;
